import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  addDoc
} from "firebase/firestore";
import { db } from "./firebase";
import useUserData from "../hooks/useUserData";
import { useNavigate } from "react-router-dom";
import MUIDataTable from "mui-datatables";
import "./css/CustomLook.css";
import { getCachedRoster, bumpRosterVersion, clearCachedRoster } from "./RosterCache";

const TeamLeaderPanel = () => {
  const { userData, loading } = useUserData();
  const navigate = useNavigate();

  const isLeader = userData?.role === "leader";

  // Tabs
  const TABS = useMemo(
    () => ({
      THEME: "theme",
      MEMBERS: "members",
      ROSTER: "roster",
      EQUIPMENT: "equipment"
    }),
    []
  );

  const [activeTab, setActiveTab] = useState(TABS.THEME);

  // Team + Theme
  const [team, setTeam] = useState(null);
  const [mainColor, setMainColor] = useState("#330055");
  const [hoverColor, setHoverColor] = useState("#2a2a2a");
  const [textColor, setTextColor] = useState("#ffffff");

  // Members
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  // Roster
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(true);

  // Shared button styles
  const buttonStyle = {
    padding: "6px 12px",
    backgroundColor: "var(--theme-color)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.9rem"
  };

  const panelCardStyle = {
  backgroundColor: "#2a2a2a",
  border: "1px solid #444",
  borderRadius: "8px",
  padding: "1rem",
  boxShadow: "0 4px 12px rgba(0,0,0,0.25)"
};

  const dangerButtonStyle = {
    padding: "6px 12px",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.9rem",
    backgroundColor: "var(--theme-color)",
    marginLeft: "0.5rem"
  };

  // Auth redirects
  useEffect(() => {
    if (!loading) {
      if (!userData) {
        navigate("/starsonata");
      } else if (userData.teamId === "unassigned") {
        navigate("/join");
      } else if (!userData.verified) {
        navigate("/starsonata");
      }
    }
  }, [userData, loading, navigate]);

  // Helpers
  function secureRandomStringWithDashes(totalLength = 20, groupSize = 5) {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZ" +
      "abcdefghijklmnopqrstuvwxyz" +
      "0123456789";

    const randomValues = new Uint8Array(totalLength);
    window.crypto.getRandomValues(randomValues);

    const raw = Array.from(randomValues, (v) => chars[v % chars.length]).join("");

    const parts = raw.match(new RegExp(`.{1,${groupSize}}`, "g"));
    return parts.join("-");
  }

  async function syncUserAuth({ username, uniqueid, teamName, teamId }) {
    if (!username || !uniqueid || !teamId) {
      throw new Error("username, uniqueid and teamId are required");
    }

    const res = await fetch("https://imp.slypandainteractive.com/updatelog.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        username,
        uniqueid,
        teamName,
        teamId
      }).toString()
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => res.statusText);
      throw new Error(`Sync failed (${res.status}): ${txt}`);
    }

    return res.text();
  }

  // Theme apply
  const handleApplyColors = async () => {
    const teamRef = doc(db, "teams", userData.teamId);
    await updateDoc(teamRef, {
      themeColor: mainColor,
      hoverColor: hoverColor,
      textColor: textColor
    });

    document.documentElement.style.setProperty("--theme-color", mainColor);
    document.documentElement.style.setProperty("--theme-hover-color", hoverColor);
    document.documentElement.style.setProperty("--theme-text-color", textColor);

    alert("Theme updated!");
  };

  // Load team theme settings
  useEffect(() => {
    if (userData?.teamId) {
      const teamRef = doc(db, "teams", userData.teamId);
      getDoc(teamRef).then((snap) => {
        if (snap.exists()) {
          const teamData = snap.data();
          setTeam(teamData);
          setMainColor(teamData.themeColor || "#330055");
          setHoverColor(teamData.hoverColor || "#2a2a2a");
          setTextColor(teamData.textColor || "#ffffff");
        }
      });
    }
  }, [userData?.teamId]);

  // Fetch team members
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!userData?.teamId || !isLeader) return;
      setLoadingMembers(true);

      const q = query(collection(db, "users"), where("teamId", "==", userData.teamId));
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      setMembers(users);
      setLoadingMembers(false);
    };

    if (isLeader) {
      fetchTeamMembers();
    } else {
      setLoadingMembers(false);
    }
  }, [userData?.teamId, isLeader]);

  // Token generation + sync
  const handleTokenGeneration = async (userId, username) => {
    if (
      !window.confirm(
        "Are you sure you want to generate a token for this user? This will delete any old tokens they have."
      )
    )
      return;

    const prefix = (userData?.teamName || "TEAM").slice(0, 3);
    const newToken = prefix + "-" + secureRandomStringWithDashes();

    await updateDoc(doc(db, "users", userId), { userToken: newToken });

    try {
      const msg = await syncUserAuth({
        username,
        uniqueid: newToken,
        teamName: userData.teamName,
        teamId: userData.teamId
      });
      console.log(msg);
    } catch (err) {
      console.error("Sync failed:", err);
      alert("Failed to sync auth table: " + err.message);
    }

    setMembers((prev) =>
      prev.map((m) => (m.id === userId ? { ...m, userToken: newToken } : m))
    );
  };

  const handleApprove = async (userId) => {
    await updateDoc(doc(db, "users", userId), { verified: true });
    setMembers((prev) => prev.map((m) => (m.id === userId ? { ...m, verified: true } : m)));
  };

  const handleRemove = async (userId) => {
    await updateDoc(doc(db, "users", userId), {
      teamId: "unassigned",
      teamName: "Unassigned",
      verified: false
    });
    setMembers((prev) => prev.filter((m) => m.id !== userId));
  };

  // Load roster (cached)
  useEffect(() => {
    if (userData?.teamId) {
      setLoadingRoster(true);
      getCachedRoster(userData.teamId)
        .then((data) => setRoster(data))
        .finally(() => setLoadingRoster(false));
    } else {
      setLoadingRoster(false);
    }
  }, [userData?.teamId]);

  const handleDeleteRosterEntry = async (entryId) => {
    const ref = doc(db, "teams", userData.teamId, "Roster", entryId);
    await deleteDoc(ref);
    setRoster((prev) => prev.filter((e) => e.id !== entryId));
    await bumpRosterVersion(userData.teamId);
    clearCachedRoster();
  };

  // Roster Form component
  const RosterForm = () => {
    const [mainCharacter, setMainCharacter] = useState("");
    const [altCharacters, setAltCharacters] = useState("");

    const handleSubmit = async (e) => {
      e.preventDefault();
      const ref = collection(db, "teams", userData.teamId, "Roster");

      await addDoc(ref, {
        mainCharacterName: mainCharacter.trim(),
        altCharacters: altCharacters
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      });

      const snapshot = await getDocs(ref);
      setRoster(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));

      setMainCharacter("");
      setAltCharacters("");

      await bumpRosterVersion(userData.teamId);
      clearCachedRoster();
    };

    return (
      <form
        onSubmit={handleSubmit}
        style={{
          marginTop: "1rem",
          border: "1px solid #ccc",
          padding: "1rem",
          borderRadius: "8px"
        }}
      >
        <h3>Add To Roster</h3>
        <div style={{ marginBottom: "0.5rem" }}>
          <label>Main Character: </label>
          <input
            type="text"
            value={mainCharacter}
            onChange={(e) => setMainCharacter(e.target.value)}
            required
          />
        </div>

        <div>
          <label>Alternative Characters (comma separated): </label>
          <input
            type="text"
            value={altCharacters}
            onChange={(e) => setAltCharacters(e.target.value)}
            required
          />
        </div>

        <button
          type="submit"
          style={{
            marginTop: "0.75rem",
            padding: "10px 20px",
            backgroundColor: "var(--theme-color)",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "1rem"
          }}
        >
          Add Roster Entry
        </button>
      </form>
    );
  };

  // Members table columns
  const memberColumns = useMemo(
    () => [
      {
        name: "username",
        label: "Username",
        options: { setCellProps: () => ({ style: { textAlign: "center" } }) }
      },
      {
        name: "email",
        label: "Email",
        options: { setCellProps: () => ({ style: { textAlign: "center" } }) }
      },
      {
        name: "userToken",
        label: "User Token (Click to Copy)",
        options: {
          customBodyRender: (value) => (
            <span
              style={{
                cursor: value ? "pointer" : "default",
                color: value ? textColor : "inherit"
              }}
              onClick={() => {
                if (!value) return;
                navigator.clipboard
                  .writeText(value)
                  .then(() => alert("Copied token to clipboard!"))
                  .catch(() => alert("Failed to copy token."));
              }}
            >
              {value || "None"}
            </span>
          ),
          setCellProps: () => ({ style: { textAlign: "center" } })
        }
      },
      {
        name: "verified",
        label: "Verified",
        options: {
          customBodyRender: (value) => (value ? "✅" : "⏳"),
          setCellProps: () => ({ style: { textAlign: "center" } })
        }
      },
      {
        name: "id",
        label: "Actions",
        options: {
          customBodyRender: (id) => {
            const member = members.find((m) => m.id === id);

            if (member?.verified) {
              return (
                <div style={{ display: "inline-flex", gap: "8px", alignItems: "center" }}>
                  <button
                    onClick={() => {
                      if (
                        window.confirm("Are you sure you want to remove this user from the team?")
                      ) {
                        handleRemove(id);
                      }
                    }}
                    style={dangerButtonStyle}
                  >
                    Boot
                  </button>

                  <button
                    onClick={() => handleTokenGeneration(id, member.username)}
                    style={buttonStyle}
                  >
                    Create Token
                  </button>
                </div>
              );
            }

            return (
              <button onClick={() => handleApprove(id)} style={buttonStyle}>
                Approve
              </button>
            );
          }
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, textColor]
  );

  // Roster table config
  const rosterTableOptions = useMemo(
    () => ({
      selectableRows: "none",
      responsive: "standard",
      elevation: 0,
      rowsPerPage: 10,
      setTableProps: () => ({ className: "custom-table-root" })
    }),
    []
  );

  const rosterColumns = useMemo(
    () => [
      {
        name: "mainCharacterName",
        label: "Main Character",
        options: { filter: false, sort: true }
      },
      {
        name: "altCharacters",
        label: "Alt Characters",
        options: {
          customBodyRender: (value) => (Array.isArray(value) ? value.join(", ") : ""),
          filter: false,
          sort: false
        }
      },
      {
        name: "actions",
        label: "Actions",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const entry = roster[dataIndex];

            return (
              <>
                <button
                  onClick={async () => {
                    const newMain = prompt("Edit main character name:", entry.mainCharacterName);
                    if (!newMain) return;

                    const newAlts = prompt(
                      "Edit alt characters (comma separated):",
                      entry.altCharacters.join(", ")
                    );
                    if (newAlts == null) return;

                    const ref = doc(db, "teams", userData.teamId, "Roster", entry.id);
                    await updateDoc(ref, {
                      mainCharacterName: newMain.trim(),
                      altCharacters: newAlts
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean)
                    });

                    const snap = await getDocs(collection(db, "teams", userData.teamId, "Roster"));
                    setRoster(snap.docs.map((d) => ({ id: d.id, ...d.data() })));

                    await bumpRosterVersion(userData.teamId);
                    clearCachedRoster();
                  }}
                  style={buttonStyle}
                >
                  Edit
                </button>

                <button
                  onClick={() => {
                    if (window.confirm(`Delete "${entry.mainCharacterName}" from roster?`)) {
                      handleDeleteRosterEntry(entry.id);
                    }
                  }}
                  style={dangerButtonStyle}
                >
                  Delete
                </button>
              </>
            );
          }
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roster, userData?.teamId]
  );

  // Tab UI component
  const TabButton = ({ label, tab }) => (
    <button
      onClick={() => setActiveTab(tab)}
      style={{
        padding: "8px 16px",
        borderRadius: "6px",
        border: "none",
        cursor: "pointer",
        fontWeight: "bold",
        backgroundColor: activeTab === tab ? "var(--theme-color)" : "#333",
        color: "#fff"
      }}
    >
      {label}
    </button>
  );

  // Tab content renderers
  const renderThemeTab = () => (
    <>
      <h3 style={{ textAlign: "center", marginTop: "0.5rem" }}>Website Look & Feel</h3>

      {team && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem",
            margin: "0 auto",
            maxWidth: "400px",
            padding: "1rem"
          }}
        >
          <label>
            Main Color:{" "}
            <input type="color" value={mainColor} onChange={(e) => setMainColor(e.target.value)} />
          </label>
          <label>
            Hover Color:{" "}
            <input type="color" value={hoverColor} onChange={(e) => setHoverColor(e.target.value)} />
          </label>
          <label>
            Main Text Color:{" "}
            <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
          </label>

          <button
            onClick={handleApplyColors}
            style={{
              backgroundColor: mainColor,
              color: textColor,
              padding: "10px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            Apply Theme Colors
          </button>
        </div>
      )}
    </>
  );

const renderMembersTab = () => (
  <div className="custom-table-wrap">
    <MUIDataTable
      title={"Registered Members"}
      data={members}
      columns={memberColumns}
      options={{
        selectableRows: "none",
        responsive: "standard",
        elevation: 0,
        rowsPerPage: 10,
        setTableProps: () => ({ className: "custom-table-root" })
      }}
    />
  </div>
);

const renderRosterTab = () => (
  <div className="custom-table-wrap">
    <MUIDataTable
      title={"Team Roster"}
      data={roster}
      columns={rosterColumns}
      options={rosterTableOptions}
    />
    <RosterForm />
  </div>
);

  const renderEquipmentTab = () => (
    <div style={{ marginTop: "0.5rem", textAlign: "center" }}>
      <h3>Team Equipment</h3>
      <div
        style={{
          marginTop: "1rem",
          padding: "1.25rem",
          borderRadius: "8px",
          border: "1px solid #ccc",
          opacity: 0.8
        }}
      >
        <p style={{ margin: 0 }}>
          Placeholder for custom trade bay list.
          <br />
          Coming Soon.. ETA before SS 3.
        </p>
      </div>
    </div>
  );

  // Loading gate
  if (loading || loadingMembers || loadingRoster) return <p>Loading...</p>;

  return (
<div
  style={{
    border: "1px solid #444",
    borderRadius: "10px",
    padding: "1rem",
    backgroundColor: "#1e1e1e",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)"
  }}
>
  {/* TAB BAR */}
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      gap: "0.5rem",
      marginBottom: "1rem",
      flexWrap: "wrap"
    }}
  >
    <TabButton label="Website Look & Feel" tab={TABS.THEME} />
    <TabButton label="Registered Members" tab={TABS.MEMBERS} />
    <TabButton label="Roster Management" tab={TABS.ROSTER} />
    <TabButton label="Team Equipment" tab={TABS.EQUIPMENT} />
  </div>

      {/* TAB CONTENT */}
       <div
    style={{
      padding: "1rem",
      borderRadius: "6px",
      backgroundColor: "#2a2a2a"
    }}
  >
    {activeTab === TABS.THEME && renderThemeTab()}
    {activeTab === TABS.MEMBERS && renderMembersTab()}
    {activeTab === TABS.ROSTER && renderRosterTab()}
    {activeTab === TABS.EQUIPMENT && renderEquipmentTab()}
  </div>
    </div>
  );
};

export default TeamLeaderPanel;
